import { influencer, outreach, TiktokAudio } from '@round/api';
import { getTableMetaHelper } from '@round/ui-kit';
import type { CellContext as LibCellContext, ColumnDef, SortingState } from '@tanstack/react-table';
import { tableHelpers } from '@round/creatorbase-ui';
import { mapOrderingToTableSorting, mapTableSortingToOrdering } from '@round/whitelabel-ui';
import { formatNumberToKNotation, formatTo2dp, numberWithCommas } from 'helpers';

export const PAGE_SIZE = 25;

export const NO_VALUE = '—';

/** The only two fields the endpoint can order by — anything else is a 400. */
const SORTABLE_COLUMN_IDS = ['created_at', 'deadline'] as const;

type SortableColumnId = (typeof SORTABLE_COLUMN_IDS)[number];

/** Newest first. */
export const DEFAULT_ORDERING: outreach.OutreachRequestOrdering = '-created_at';

const isSortableColumnId = (id: string): id is SortableColumnId => SORTABLE_COLUMN_IDS.includes(id as SortableColumnId);

/** An ordering the endpoint cannot serve — an unknown column, or none at all — falls back to the default. */
export function parseOrdering(ordering: string | undefined): outreach.OutreachRequestOrdering {
    const [sort] = mapOrderingToTableSorting(ordering ? [ordering] : []);
    if (!sort || !isSortableColumnId(sort.id)) {
        return DEFAULT_ORDERING;
    }

    return ordering as outreach.OutreachRequestOrdering;
}

/** Derived from the ordering param, so the header arrow cannot claim an order the rows do not have. */
export function getSorting(ordering: string | undefined): SortingState {
    return mapOrderingToTableSorting([parseOrdering(ordering)]);
}

/** Clearing the sort falls back to the default rather than sending no ordering at all. */
export function getOrdering(sorting: SortingState): outreach.OutreachRequestOrdering {
    return parseOrdering(mapTableSortingToOrdering(sorting)[0]);
}

export function getTargetsSummary(targets: outreach.OutreachTargets): string {
    const parts = [
        getCountLabel(targets.audios.length, 'sound', 'sounds'),
        getCountLabel(targets.hashtags.length, 'hashtag', 'hashtags'),
    ].filter((part): part is string => part !== null);

    return parts.length ? parts.join(', ') : NO_VALUE;
}

const getCountLabel = (count: number, singular: string, plural: string): string | null =>
    count ? `${count} ${count === 1 ? singular : plural}` : null;

// Read off the row alone, so the summary never waits on the country lookup the card needs. A bound
// left open is left out: it narrowed nothing, and the card is where every setting is accounted for.
export function getFiltersSummary(filters: outreach.OutreachFilters): string {
    const parts = [
        getCountLabel(filters.locations.length, 'location', 'locations'),
        getFollowersSummary(filters),
        getEngagementRateSummary(filters.min_engagement_rate),
        getAvgViewsSummary(filters.min_avg_views),
    ].filter((part): part is string => part !== null);

    return parts.length ? parts.join(', ') : NO_VALUE;
}

function getFollowersSummary({ min_followers, max_followers }: outreach.OutreachFilters): string | null {
    if (typeof min_followers === 'number' && typeof max_followers === 'number') {
        return `${formatNumberToKNotation(min_followers)}–${formatNumberToKNotation(max_followers)} followers`;
    }

    if (typeof min_followers === 'number') {
        return `${formatNumberToKNotation(min_followers)}+ followers`;
    }

    if (typeof max_followers === 'number') {
        return `up to ${formatNumberToKNotation(max_followers)} followers`;
    }

    return null;
}

/** Stored as a fraction of 1, read by people as a percentage. */
function getEngagementRateSummary(minEngagementRate: number | null): string | null {
    if (typeof minEngagementRate !== 'number') {
        return null;
    }

    return `${formatTo2dp(minEngagementRate * 100)}%+ ER`;
}

function getAvgViewsSummary(minAvgViews: number | null): string | null {
    if (typeof minAvgViews !== 'number') {
        return null;
    }

    return `${formatNumberToKNotation(minAvgViews)}+ views`;
}

/** A row still loading carries no figures, and the cell standing in for it draws a skeleton anyway. */
export const getCountValue = (count: number | undefined): string =>
    typeof count === 'number' ? numberWithCommas(count) : NO_VALUE;

/** A list row carries the whole request — the list and the detail share one serializer. */
export type Row = outreach.OutreachRequest;

export type Meta = {
    isLoading: boolean;
    /** A row names its sounds by id alone, so what they are comes from a second read. */
    audiosById: Map<number, TiktokAudio>;
    /** Whether the sounds for the page this request came back on are still on their way. */
    getIsAudiosLoading: (requestId: number) => boolean;
    /** Locations are ids too, but one cached country list names them for every row at once. */
    countriesById: Map<number, influencer.InfluencerLocation>;
    isLoadingCountries: boolean;
};

export const getMeta = getTableMetaHelper<Meta>();

export type CellContext = LibCellContext<tableHelpers.WithLoadingRow<Row>, unknown>;

// `any` (not `unknown`) so string-valued cells like tableHelpers.TruncatedCell,
// whose context is typed to a string value, satisfy the shared column type.
export type Column = ColumnDef<tableHelpers.WithLoadingRow<Row>, any>;
